<template>
    <div class="flex flex-col">
        <label
            v-if="label"
            class="text-label font-semibold mb-2 text-gray-800"
            :class="labelClasses"
        >
            {{ label }}
            <span v-if="required"> * </span>
        </label>
        <div
            class="relative rounded-md focus-within:text-gray-900 text-gray-500"
        >
            <div
                v-if="$slots.prepend"
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                :class="prependClass"
            >
                <slot name="prepend" />
            </div>
            <div
                v-if="inputValue && readonly"
                class="w-full p-2 rounded-lg border bg-gray-75 text-body text-gray-800 cursor-pointer"
            >
                {{ inputValue }}
            </div>
            <input
                v-else
                id="input"
                ref="inputRef"
                v-model="inputValue"
                class="rounded-lg border placeholder:italic active:outline-none text-body w-full"
                :type="type"
                :placeholder="placeholder"
                :class="inputClasses"
                :name="name"
                :autocomplete="autoComplete"
                :inputmode="inputMode"
                :readonly="readonly"
                :disabled="disabled"
                :data-test="name"
                :tabindex="tabindex"
                @focus="onFocus()"
                @blur="onBlur()"
            />
            <div
                v-if="$slots.append"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
                <slot name="append" />
            </div>
        </div>
        <slot />
        <div v-if="error || info" class="text-error-message ml-2 h-5">
            <span v-if="error" class="text-alert">{{ error }}</span>
            <span v-if="info && !error" class="text-gray-700">
                {{ info }}
            </span>
        </div>
    </div>
</template>

<script setup>
import { flagPasteOrDrop } from "@/Helpers/useFraudProtection";
import { fieldProps, useFieldSetup } from "@/Mixins/FormFieldMixin";
import { ref, useSlots } from "vue";
import { computed, onMounted } from "vue";

const props = defineProps({
    ...fieldProps,
    readonly: {
        type: Boolean,
        default: false,
    },
    inputMode: {
        type: String,
        default: "text",
        validator: (val) => {
            return [
                "none",
                "text",
                "decimal",
                "numeric",
                "tel",
                "search",
                "email",
                "url",
            ].includes(val);
        },
    },
    inputClass: {
        type: Array,
        default: () => [],
    },
    prependClass: {
        type: String,
        default: "",
    },
    type: {
        type: String,
        default: "text",
        validator: (val) => {
            return [
                "url",
                "text",
                "password",
                "email",
                "search",
                "tel",
                "number",
            ].includes(val);
        },
    },
    tabindex: {
        type: Number,
        default: 0,
    },
    visualizeFocus: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(["update:modelValue", "focus", "blur"]);
const { inputValue, onChange, onFocus, onBlur } = useFieldSetup(props, emit);
const slots = useSlots();
const labelClasses = computed(() => ({
    "text-label-sm": props.sm,
    "text-label-lg": props.lg,
}));

const inputClasses = computed(() => [
    ...props.inputClass,
    "text-gray-800",
    {
        "border-alert ring-1 ring-alert": props.error,
        "py-2": !props.sm && !props.lg,
        "py-1 text-subtitle-1": props.sm,
        "py-3 text-lead-1": props.lg,
        "pl-11": slots.prepend,
        "pr-11": slots.append,
        "bg-gray-75 border-gray-200": props.readonly,
        "bg-white border-gray-500": !props.readonly,
    },
    !props.visualizeFocus || props.readonly
        ? "focus:outline-0 focus:ring-0"
        : "focus:border-gray-900 focus:ring-1 focus:ring-gray-900",
]);

const inputRef = ref(null);

onMounted(() => {
    if (props.flagPasteOrDrop && inputRef.value) {
        flagPasteOrDrop(inputRef.value);
    }
});
</script>
